import React, { FC, MouseEvent, useState } from 'react';
import { t } from 'i18next';
import {
  ResizeBlock,
  SubReportsContainer,
  ShareResultsTileCont,
  ShareResultsTileContentCont,
  ShareResultsTileGroup,
  ShareResultsTileAccess,
  ShareResultsTileResponseRate,
  ShareResultsTileWrapper,
} from './ShareResultsTile.styles';
import PencilIcon from '@/assets/icons/pencil.svg?react';
import CloseAll from '@/assets/icons/closeAllAccordion.svg?react';
import OpenAll from '@/assets/icons/openAllAccordion.svg?react';
import { NoAccess } from '../ShareResults.styles';
import ResponseRate from '@/components/ResponseRate/ResponseRate';
import { IItemReportResponse } from '@/services/reports';

interface ShareResultsTileProps {
  item: IItemReportResponse;
  processReports: () => Promise<void>;
  setSelectedReport: (id: string) => void;
  depth?: number;
  isLastChild?: boolean;
  isVisualLastChild?: boolean;
}

const ShareResultsTile: FC<ShareResultsTileProps> = ({
  item,
  processReports,
  setSelectedReport,
  depth = 0,
  isLastChild = false,
  isVisualLastChild = false,
}) => {
  const [active, setActive] = useState<boolean>(false);
  const { accessList, responsiveness, id, group, sub_reports } = item;

  const handleShowSubReports = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setActive(active => !active);
  };

  const shouldPropagateVisualLast = depth === 0 ? true : isVisualLastChild;
  const isExpanded = Boolean(active && sub_reports?.length);

  return (
    <>
      <ShareResultsTileWrapper>
        <ShareResultsTileCont
          depth={depth}
          isLastChild={isLastChild}
          isVisualLastChild={isVisualLastChild}
          isExpanded={isExpanded}
          isActive={active}
        >
          <ShareResultsTileContentCont>
            {sub_reports?.length ? (
              <ResizeBlock
                onClick={handleShowSubReports}
                style={{ cursor: 'pointer' }}
              >
                {active ? <CloseAll /> : <OpenAll />}
              </ResizeBlock>
            ) : (
              <ResizeBlock />
            )}
            <ShareResultsTileGroup>{group}</ShareResultsTileGroup>
            <ShareResultsTileAccess>
              {accessList?.users?.length ? (
                accessList.users.map(acc => acc.name || acc.email || ' - ').join(', ')
              ) : (
                <NoAccess>{t('viewSurvey.sharing.tableBody.noAccess')}</NoAccess>
              )}
              <PencilIcon
                onClick={() => {
                  (async () => await processReports())();
                  setSelectedReport(id);
                }}
              />
            </ShareResultsTileAccess>
            <ShareResultsTileResponseRate>
              <ResponseRate
                mode={responsiveness.representativeness}
                percent={responsiveness.responseRate}
                responseCount={responsiveness.responseCount}
                maxResponseCount={responsiveness.participantCount}
              />
            </ShareResultsTileResponseRate>
          </ShareResultsTileContentCont>
        </ShareResultsTileCont>
      </ShareResultsTileWrapper>
      {active && sub_reports?.length ? (
        <SubReportsContainer $depth={depth}>
          {sub_reports.map((subReport, index) => (
            <ShareResultsTile
              key={subReport.id}
              item={subReport}
              processReports={processReports}
              setSelectedReport={setSelectedReport}
              depth={depth + 1}
              isLastChild={index === sub_reports.length - 1}
              isVisualLastChild={
                shouldPropagateVisualLast && index === sub_reports.length - 1
              }
            />
          ))}
        </SubReportsContainer>
      ) : null}
    </>
  );
};

export default ShareResultsTile;
